import { useEffect, useState, useCallback } from "react";
import { Megaphone, MessageSquareText, Send } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge, Empty } from "@/components/ui-kit";

export default function Services() {
  const { user } = useAuth();
  const isStaff = user.role === "ADMIN" || user.role === "PETUGAS_LAPANG";
  const isAdmin = user.role === "ADMIN";
  const [tickets, setTickets] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [ticketForm, setTicketForm] = useState({ category: "Layanan Anggota", subject: "", message: "" });
  const [announceForm, setAnnounceForm] = useState({ title: "", body: "", audience: "PUBLIC" });
  const [reply, setReply] = useState({});

  const load = useCallback(() => {
    api.get("/tickets").then((r) => setTickets(r.data.items)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const createTicket = async () => {
    try { await api.post("/tickets", ticketForm); setTicketForm({ category: "Layanan Anggota", subject: "", message: "" }); load(); }
    catch { alert("Gagal membuat tiket"); }
  };

  const createAnnouncement = async () => {
    try { await api.post("/announcements", announceForm); setAnnounceForm({ title: "", body: "", audience: "PUBLIC" }); load(); }
    catch { alert("Gagal membuat pengumuman"); }
  };

  const updateStatus = async (ticket, status) => {
    try { await api.put(`/tickets/${ticket.ticket_id}/status`, { status }); load(); }
    catch { alert("Gagal mengubah status"); }
  };

  const sendReply = async (ticket) => {
    try { await api.post(`/tickets/${ticket.ticket_id}/reply`, { message: reply[ticket.ticket_id] }); setReply({ ...reply, [ticket.ticket_id]: "" }); load(); }
    catch { alert("Gagal membalas tiket"); }
  };

  return (
    <div data-testid="services-page">
      <PageHeader kicker="Komunikasi & Layanan" title="Layanan Koperasi" desc={isStaff ? "Kelola tiket layanan, pengumuman, dan tindak lanjut warga/anggota." : "Ajukan layanan dan pantau pengumuman koperasi."} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <section>
          <div className="flex items-center gap-2 mb-4"><MessageSquareText size={18} /><h2 className="font-bold text-xl">Tiket Layanan</h2></div>
          {!isStaff && (
            <div className="border hairline p-5 mb-5 grid grid-cols-1 gap-3">
              <input className="field tap px-4" placeholder="Subjek" value={ticketForm.subject} onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })} />
              <select className="field tap px-4" value={ticketForm.category} onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}>
                <option>Layanan Anggota</option><option>BBM & Kuota</option><option>Simpan Pinjam</option><option>Keluhan Warga</option>
              </select>
              <textarea className="field px-4 py-3" rows={3} placeholder="Ceritakan kebutuhan Anda" value={ticketForm.message} onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })} />
              <button disabled={!ticketForm.subject || !ticketForm.message} onClick={createTicket} className="tap btn-primary font-semibold disabled:opacity-40"><Send size={16} className="inline mr-2" /> Kirim Tiket</button>
            </div>
          )}
          {tickets.length === 0 ? <Empty>Belum ada tiket layanan.</Empty> : (
            <div className="border hairline divide-y divide-[var(--line)]">
              {tickets.map((t) => (
                <div key={t.ticket_id} className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{t.subject}</p>
                        <Badge tone={t.status === "RESOLVED" ? "ok" : t.status === "IN_PROGRESS" ? "ink" : "outline"}>{t.status}</Badge>
                      </div>
                      <p className="text-sm text-[var(--muted)] mt-1">{t.category} · {t.contact_name}</p>
                      <p className="text-sm mt-3">{t.message}</p>
                    </div>
                  </div>
                  {isStaff && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
                      <input className="field tap px-4" placeholder="Balas singkat" value={reply[t.ticket_id] || ""} onChange={(e) => setReply({ ...reply, [t.ticket_id]: e.target.value })} />
                      <button onClick={() => sendReply(t)} disabled={!reply[t.ticket_id]} className="btn-outline px-3 py-2 text-sm font-semibold disabled:opacity-40">Balas</button>
                      <button onClick={() => updateStatus(t, t.status === "RESOLVED" ? "OPEN" : "RESOLVED")} className="btn-primary px-3 py-2 text-sm font-semibold">{t.status === "RESOLVED" ? "Buka Lagi" : "Selesai"}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-2 mb-4"><Megaphone size={18} /><h2 className="font-bold text-xl">Pengumuman</h2></div>
          {isAdmin && (
            <div className="border hairline p-5 mb-5 grid grid-cols-1 gap-3">
              <input className="field tap px-4" placeholder="Judul" value={announceForm.title} onChange={(e) => setAnnounceForm({ ...announceForm, title: e.target.value })} />
              <textarea className="field px-4 py-3" rows={3} placeholder="Isi pengumuman" value={announceForm.body} onChange={(e) => setAnnounceForm({ ...announceForm, body: e.target.value })} />
              <select className="field tap px-4" value={announceForm.audience} onChange={(e) => setAnnounceForm({ ...announceForm, audience: e.target.value })}>
                <option value="PUBLIC">Publik</option><option value="MEMBERS">Anggota</option><option value="STAFF">Petugas</option>
              </select>
              <button onClick={createAnnouncement} disabled={!announceForm.title || !announceForm.body} className="tap btn-primary font-semibold disabled:opacity-40">Terbitkan</button>
            </div>
          )}
          {announcements.length === 0 ? <Empty>Belum ada pengumuman.</Empty> : (
            <div className="border hairline divide-y divide-[var(--line)]">
              {announcements.map((a) => (
                <div key={a.announcement_id} className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <p className="font-semibold">{a.title}</p>
                    <Badge tone="lav">{a.audience}</Badge>
                  </div>
                  <p className="text-sm text-[var(--muted)] mt-2">{a.body}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
